<script setup lang="ts">
import api from "@/api";
import toRRuleInput from "@/helpers/rrules/toRRuleInput";
import TextSkeleton from "@/stories/skeletons/TextSkeleton/TextSkeleton.vue";
import type { RRuleState } from "@/types";
import { onMounted, ref, watch } from "vue";
import RRuleTextDisplayer from "@/stories/displays/RRuleText/RRuleTextDisplayer.vue";
import type { OccurrencesResult } from "@/api/repeat";
import isValidRRule from "@/helpers/rrules/isValidRRule";

interface Props {
  modelValue: RRuleState;
  displayTime?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  displayTime: true,
});

const emit = defineEmits<{
  "update:modelValue": [RRuleState];
  isValid: [boolean];
}>();

const isLoading = ref(false);
const cache = new Map<string, OccurrencesResult>();

const update = (occurrences?: OccurrencesResult) => {
  emit("update:modelValue", {
    ...props.modelValue,
    occurrences: occurrences,
  });
};

// Generate stable cache key from rule-defining fields only (excludes occurrences)
const getCacheKey = (state: RRuleState): string => {
  return JSON.stringify(toRRuleInput(state));
};

const calculateOccurrences = async (optionsHash: string) => {
  try {
    const occurrences = await api.repeat.getOccurrences({
      rset: { rule: toRRuleInput(props.modelValue) },
    });
    update(occurrences);
    // Store in cache
    cache.set(optionsHash, occurrences);
  } finally {
    isLoading.value = false;
  }
};

const checkStatusAndUpdate = (optionsHash: string) => {
  const hasRepeatAmount =
    props.modelValue.repeatAmount && props.modelValue.repeatAmount > 0;

  const hasEndingDate =
    props.modelValue.endingDate && props.modelValue.endingDate > 0;

  const isInfinite = props.modelValue.endingType === "no_end_date";

  // If cached, update immediately without debounce
  if (cache.has(optionsHash)) {
    update(cache.get(optionsHash));
  } else if (hasRepeatAmount || hasEndingDate || isInfinite) {
    // Show loading and debounce for uncached values
    isLoading.value = true;
    calculateOccurrences(optionsHash);
  } else {
    update({ status: "error", data: [] });
    emit("isValid", false);
  }
};

watch(
  () => getCacheKey(props.modelValue),
  (optionsHash) => {
    checkStatusAndUpdate(optionsHash);
  },
);

onMounted(() => {
  checkStatusAndUpdate(getCacheKey(props.modelValue));
});

watch(
  () => props.modelValue.occurrences,
  () => {
    emit("isValid", isValidRRule(props.modelValue.occurrences));
  },
);
</script>

<template>
  <TextSkeleton v-if="isLoading" class="w-full max-w-[30rem] h-[2.75rem]" />
  <RRuleTextDisplayer v-else :state="modelValue" :displayTime="displayTime" />
</template>
